import React, {useContext, useEffect, useState} from 'react';
import {useSnackbar} from 'notistack';
import AppContext from '../../Services/AppContext';
import JString from '@easytech-international-sdn-bhd/jstring';
import API_Service, {axiosDefaultConfig, axiosPutConfig} from '../../Services/API_Service';
import PersistantData from '../../Services/PersistantData';
import {req_survey_forms, req_survey_form} from '../../Helper/Constants';
import {makeStyles} from '@material-ui/core/styles';
import {Chip, Typography, IconButton, Tooltip} from "@material-ui/core";
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import {grey, green, orange, red} from '@material-ui/core/colors';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';
import ShimmerLoading from '../../Components/ShimmerLoading';
import {useCommonStyles} from '../../Styles';
import SurveyFormBuilder from '../../Components/SurveyFormBuilder';

const useStyles = makeStyles((theme) => ({
    sectionTitle: {
        color: grey[500],
        marginLeft: theme.spacing(1),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
    left: {
        marginLeft: theme.spacing(1)
    },
    fontGrey: {
        color: 'grey'
    },
    marginTop: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3)
    }
}));

const baseHeadCells = [
    {
        name: "id",
        label: "ID",
        options: {
            display: false
        }
    },
    {
        name: "title",
        label: "Title",
        options: {
            display: true,
            filterType: 'textField',
            customBodyRender: (value) => {
                return <Typography variant="body2">{value}</Typography>;
            }
        }
    },
    {
        name: "description", 
        label: "Description",
        options: {
            display: true,
            filterType: 'textField',
            customBodyRender: (value) => {
                return <Typography variant="body2" style={{maxWidth: 200}} noWrap>
                    {value || '-'}
                </Typography>;
            }
        }
    },
    {
        name: "status",
        label: "Status",
        options: {
            display: true,
            filterType: 'dropdown',
            filterOptions: {
                names: ['draft', 'active', 'closed']
            },
            customBodyRender: (value) => {
                const statusConfig = {
                    draft: {color: grey[500], label: 'Draft', icon: EditIcon},
                    active: {color: green[500], label: 'Active', icon: DoneIcon},
                    closed: {color: red[500], label: 'Closed', icon: CloseIcon}
                };
                const config = statusConfig[value] || statusConfig.draft;
                return (
                    <Chip 
                        size="small" 
                        label={config.label}
                        style={{color: config.color, borderColor: config.color}}
                        variant="outlined"
                        icon={<config.icon style={{color: config.color}} />}
                    />
                );
            }
        }
    },
    {
        name: "questions_count",
        label: "Questions Count",
        options: {
            display: true,
            filter: false,
            customBodyRender: (value) => {
                return <Typography variant="body2">{value || 0} Questions</Typography>;
            }
        }
    },
    {
        name: "submissions_count",
        label: "Submissions Count",
        options: {
            display: true,
            filter: false,
            customBodyRender: (value) => {
                return <Typography variant="body2">{value || 0} Submissions</Typography>;
            }
        }
    },
    {
        name: "created_by",
        label: "Created By",
        options: {
            display: true,
            filterType: 'textField'
        }
    },
    {
        name: "created_at",
        label: "Created At",
        options: {
            display: true,
            filter: false,
            customBodyRender: (value) => {
                return moment(value).format('YYYY-MM-DD HH:mm');
            }
        }
    },
    {
        name: "updated_at",
        label: "Updated At",
        options: {
            display: false,
            filter: false,
            customBodyRender: (value) => {
                return moment(value).format('YYYY-MM-DD HH:mm');
            }
        }
    }
];

function SurveyForm() {
    const classes = useStyles();
    const commonStyles = useCommonStyles();
    const [gState] = useContext(AppContext);
    
    const [surveyList, setSurveyList] = useState([]);
    const [closedSurveyList, setClosedSurveyList] = useState([]);
    const [showActive, setShowActive] = useState(true);

    const [axiosConfig, setAxiosConfig] = useState({});
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingSurvey, setEditingSurvey] = useState(null);
    const {enqueueSnackbar} = useSnackbar();
    
    const singleton = PersistantData.getInstance();
    var {translation: language} = gState;
    if (typeof (language) === 'undefined') {
        language = singleton.getlang();
    }

    useEffect(() => {
        if (JString(gState.client).isValid()) {
            var axiosGetConfig = axiosDefaultConfig(gState.client);
            setAxiosConfig(axiosGetConfig);
            loadSurveyList(axiosGetConfig);
        }
    }, [gState.client]);

    const loadSurveyList = (axiosRequestConfig = axiosConfig) => {
        setLoading(true);
        API_Service.get(req_survey_forms, axiosRequestConfig)
            .then(({data}) => {
                const {result, extraData} = data;
                console.log('extraData', extraData);
                if (result === 1) {
                    setSurveyList(extraData.not_closed_list || []);
                    setClosedSurveyList(extraData.closed_list || []);
                } else {
                    enqueueSnackbar('Empty Form List', {variant: 'warning'});
                }
            })
            .catch((error) => {
                console.error('Load survey list error:', error);
                enqueueSnackbar('Failed to get survey list', {variant: 'error'});
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const loadSurveyDetails = (surveyId) => {
        return API_Service.get(`${req_survey_form}/${surveyId}`, axiosConfig)
            .then(({data}) => {
                const {result, extraData} = data;
                if (result === 1) {
                    return extraData;
                } else {
                    throw new Error('Failed to load survey details');
                }
            })
            .catch((error) => {
                console.error('Load survey details error:', error);
                enqueueSnackbar('Failed to load survey details', {variant: 'error'});
                throw error;
            });
    };

    const handleEdit = async (surveyId) => {
        try {
            const surveyDetails = await loadSurveyDetails(surveyId);
            setEditingSurvey(surveyDetails);
            setShowEditDialog(true);
        } catch (error) {
            enqueueSnackbar('Failed to load survey details', {variant: 'error'});
        }
    };

    const handleExport = (surveyId) => {
        console.log('Export survey:', surveyId);
        // TODO: Implement export functionality
    };

    const handleCreateNew = () => {
        setShowCreateDialog(true);
    };

    const handleCreateClose = () => {
        setShowCreateDialog(false);
    };

    const handleCreateSubmit = () => {
        loadSurveyList();
        setShowCreateDialog(false);
    };

    const handleEditClose = () => {
        setShowEditDialog(false);
        setEditingSurvey(null);
    };

    const handleEditSubmit = () => {
        loadSurveyList();
        setShowEditDialog(false);
        setEditingSurvey(null);
    };

    const getHeadCells = () => {
        return [
            ...baseHeadCells,
            {
                name: "actions",
                label: "Actions",
                options: {
                    display: true,
                    filter: false,
                    sort: false,
                    customBodyRender: (value, tableMeta) => {
                        const rowData = tableMeta.rowData;
                        const surveyId = rowData[0];
                        const status = rowData[3];
                        
                        return (
                            <div>
                                <Tooltip title="Edit">
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(surveyId);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                {/* <Tooltip title="Export Results">
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExport(surveyId);
                                        }}
                                        disabled={status === 'draft'}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </Tooltip> */}
                            </div>
                        );
                    }
                }
            }
        ];
    };

    const CustomToolbar = ({showActive}) => {
        return (
            <>
                <Tooltip title={showActive ? "Show Closed Only" : "Show Active/Draft Only"}>
                    <IconButton onClick={() => setShowActive(!showActive)}>
                        {showActive ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Create New Survey">
                    <IconButton onClick={handleCreateNew}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    const options = {
        print: false,
        filter: true,
        download: false,
        selectableRows: 'none',
        rowsPerPage: 25,
        rowsPerPageOptions: [25, 50, 100],
        responsive: 'scrollMaxHeight',
        setTableProps: () => ({size: 'small'}),
        customToolbar: () => <CustomToolbar showActive={showActive} />,
        textLabels: {
            body: {
                noMatch: loading ? <ShimmerLoading /> : "No matching survey found",
                toolTip: "Sort",
            },
            pagination: {
                next: "Next Page",
                previous: "Previous Page",
                rowsPerPage: "Rows per page:",
                displayRows: "Total",
            },
            toolbar: {
                search: "Search",
                downloadCsv: "Download CSV",
                print: "Print",
                viewColumns: "View Columns",
                filterTable: "Filter Table",
            },
            filter: {
                all: "All",
                title: "Filter",
                reset: "Reset",
            },
            viewColumns: {
                title: "View Columns",
                titleAria: "View/Hide Table Columns",
            },
        }
    };

    if (loading) {
        return <ShimmerLoading />;
    }

    return (
        <div className={commonStyles.root}>
            <MUIDataTable
                title={showActive ? "Form List" : "Closed Form List"}
                data={showActive ? surveyList : closedSurveyList}
                columns={getHeadCells()}
                options={options}
            />
            
            <SurveyFormBuilder
                gState={gState}
                open={showCreateDialog}
                onClose={handleCreateClose}
                onSubmit={handleCreateSubmit}
                axiosConfig={axiosConfig}
                language={language}
            />
            
            <SurveyFormBuilder
                gState={gState}
                open={showEditDialog}
                onClose={handleEditClose}
                onSubmit={handleEditSubmit}
                axiosConfig={axiosConfig}
                language={language}
                editData={editingSurvey}
            />
        </div>
    );
}

export default SurveyForm;